/**
 * Settings → Security → Change Password
 * Sprint: TREATX-CHANGE-PASSWORD-01
 *
 * Single canonical change-password UX for ALL roles.
 * Calls POST /api/auth/change-password (canonical, bcrypt, rbac_users SSOT).
 * On success: toast, force logout, redirect to /login.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, ShieldCheck, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const PASSWORD_MIN = 8;

function clientValidate({ current, next, confirm }) {
  if (!current) return 'Current password is required';
  if (!next) return 'New password is required';
  if (!confirm) return 'Please confirm the new password';
  if (next !== confirm) return 'New password and confirmation do not match';
  if (next === current) return 'New password must be different from current password';
  if (next.length < PASSWORD_MIN) return `New password must be at least ${PASSWORD_MIN} characters`;
  if (/^[A-Za-z]+$/.test(next) || /^\d+$/.test(next))
    return 'New password must include both letters and numbers';
  return null;
}

const PasswordField = ({ id, label, value, onChange, autoComplete, testid }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label htmlFor={id} className="text-sm text-slate-700">{label}</Label>
      <div className="relative mt-1">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className="pr-10"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          aria-label={show ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

const ChangePassword = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const err = clientValidate({ current, next, confirm });
    if (err) {
      setError(err);
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      toast.success('Password changed successfully. Please log in again.');
      // Force-logout: clears unified token + every legacy surface.
      logout();
      // Small delay so the toast paints before the redirect.
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 800);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d?.msg || JSON.stringify(d)).join(' ')
          : e?.message || 'Password change failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    // ProtectedRoute also guards this, but keep an explicit guard.
    navigate('/login', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="change-password-page">
      <div className="max-w-xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4" data-testid="change-password-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>

        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                <Lock className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg">Change Password</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Signed in as <span className="font-medium text-slate-700">{user?.email}</span>
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <PasswordField
                id="cp-current"
                label="Current Password"
                value={current}
                onChange={setCurrent}
                autoComplete="current-password"
                testid="cp-current-password"
              />
              <PasswordField
                id="cp-next"
                label="New Password"
                value={next}
                onChange={setNext}
                autoComplete="new-password"
                testid="cp-new-password"
              />
              <PasswordField
                id="cp-confirm"
                label="Confirm New Password"
                value={confirm}
                onChange={setConfirm}
                autoComplete="new-password"
                testid="cp-confirm-password"
              />

              <div className="text-xs text-slate-500 flex items-start gap-2 mt-2">
                <ShieldCheck className="h-3.5 w-3.5 mt-0.5 text-emerald-600" />
                <span>
                  Minimum {PASSWORD_MIN} characters, must include both letters and
                  numbers. After saving you will be logged out and asked to sign
                  in again with the new password.
                </span>
              </div>

              {error && (
                <div
                  className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
                  data-testid="cp-error"
                >
                  {error}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={submitting}
                  data-testid="cp-cancel"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="cp-submit">
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    'Save Password'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChangePassword;
